import type { Transition } from "motion/react";

/**
 * Design tokens for the MD3 Expressive Toolbars components.
 * Extracted from `FloatingToolbarTokens.kt` and `FabBaselineTokens.kt`.
 */

/**
 * Spring motion specification for Toolbar buttons (ToolbarIconButton, ToolbarToggleButton).
 * Synchronized with ButtonDistribute spring characteristics.
 * Per spec: framer-motion spring config (stiffness: 300, damping: 30, mass: 0.8).
 */
export const TOOLBAR_SPRING_TRANSITION: Transition = {
	type: "spring",
	stiffness: 300,
	damping: 30,
	mass: 0.8,
};

export const FloatingToolbarTokens = {
	ContainerBetweenSpace: 4,
	ContainerExternalPadding: 16,
	ContainerHeight: 64,
	ContainerLeadingSpace: 8,
	ContainerShape: "full",
	ContainerTrailingSpace: 8,
	VerticalContainerExternalPadding: 24,
} as const;

export const FabBaselineTokens = {
	ContainerHeight: 56,
	ContainerWidth: 56,
	MediumContainerHeight: 80,
	MediumContainerWidth: 80,
	IconSize: 24,
	MediumIconSize: 28,
} as const;

export const ToolbarToFabGap = 8;

/**
 * Design tokens for the ToolbarDivider component.
 * The divider is a decorative separator used to group toolbar actions.
 */
export const ToolbarDividerTokens = {
	/** Divider line thickness in px */
	Thickness: 1,
	/** Divider size as a fraction of the toolbar container dimension */
	HeightRatio: 0.5,
} as const;

/**
 * Design tokens for the ToolbarIconButton component.
 * Sizes follow MD3 Expressive touch-target minimums (48dp height always).
 * - narrow: de-emphasised secondary actions
 * - default: standard icon button
 * - wide: primary action emphasis inside the toolbar
 */
export const ToolbarIconButtonTokens = {
	NarrowWidth: 40,
	DefaultWidth: 48,
	WideWidth: 64,
	/** Minimum touch-target height per MD3 accessibility guidelines */
	Height: 48,
	/** Icon size in dp — larger than standard 24dp to fill the 48dp button properly */
	IconSize: 28,
} as const;

/**
 * Design tokens for the ToolbarToggleButton component.
 * Follows MD3 Expressive 'Button toggleable' building block specifications.
 */
export const ToolbarToggleButtonTokens = {
	Height: 48,
	MinWidth: 95,
	IconSize: 24,
	PaddingX: 16,
	Gap: 8,
} as const;

/**
 * Border-radius tokens for ToolbarToggleButton spring-press morphing.
 * pillRadius = height / 2 = 48 / 2 = 24px (fully rounded pill).
 * pressedRadius morphs to a squished shape on pointer-down.
 */
export const ToolbarToggleButtonRadiusTokens = {
	pillRadius: 24,
	pressedRadius: 12,
} as const;
